// src/services/FirebaseBrain/FirebaseBrain.js
// Веб-порт Android FirebaseBrain. Каждый метод собирает params и зовёт
// logEventSession(), который доклеивает общие параметры (как на Android).

import { logEvent, setUserId } from 'firebase/analytics';
import { analytics, analyticsReady } from './Firebase';
import { SessionTracker } from './SessionTracker';
import { FirebaseBrainScreens } from './FirebaseBrainScreens';

// --- Контекст пользователя (аналог prefs.userId / prefs.isSubscribed) ---
// Модуль не знает про auth сам — приложение задаёт контекст через setUserContext()
// после checkIsAuth(), точно так же как Android читает из prefs.
const userContext = {
  userId: null,    // стабильный web-id (анонимный) — основная идентичность на вебе
  linkedId: null,  // id связки по QR (есть только если юзер связал сайт с приложением)
  isSubscribed: false,
};

// Стабильный анонимный id: живёт между сессиями в localStorage.
const ANON_KEY = 'pt_anon_id';
function getOrCreateAnonId() {
  try {
    let id = localStorage.getItem(ANON_KEY);
    if (!id) {
      id = (crypto.randomUUID && crypto.randomUUID()) ||
        `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(ANON_KEY, id);
    }
    return id;
  } catch {
    return null; // приватный режим без localStorage
  }
}

// userId здесь = id связки по QR (то, что отдаёт getAuthCookie), а не «логин».
export function setUserContext({ userId, isSubscribed }) {
  if (isSubscribed !== undefined) userContext.isSubscribed = isSubscribed;

  // web-идентичность — всегда анонимный id, не зависит от связки
  userContext.userId = getOrCreateAnonId();

  // QR-связка (может быть null)
  const linkedId = userId || null;
  userContext.linkedId = linkedId;

  // GA4 User-ID задаём ТОЛЬКО при QR-связке — тем же id, что в мобильном приложении.
  // Это единственный мост web ↔ mobile для одного человека.
  if (linkedId) {
    if (analytics) {
      setUserId(analytics, linkedId);
    } else {
      analyticsReady.then((a) => a && setUserId(a, linkedId));
    }
  }
}

// --- Версия приложения ---
// Прокинь её через .env: REACT_APP_VERSION=1.4.0 (или возьми из package.json при сборке)
const APP_VERSION = process.env.REACT_APP_VERSION || require('../../../package.json').version;
//const BUILD_TYPE = process.env.NODE_ENV === 'production' ? 'release' : 'debug';

// Аналог Build.VERSION.RELEASE — для веба берём браузер из userAgent (по желанию)
function getBrowserInfo() {
  if (typeof navigator === 'undefined') return 'unknown';
  return navigator.userAgent;
}

// --- Cold/warm старт (аналог App.kt: now - lastExit > 30_000 → cold) ---
// На Android lastExit берётся из prefs (переживает рестарт процесса).
// На вебе процесс = загрузка страницы, поэтому единственное, что выживает, — localStorage.
const LAST_EXIT_KEY = 'pt_last_exit';
const COLD_THRESHOLD_MS = 30_000;            // как на Android: >30 c в фоне → cold

function detectLaunchType() {
  try {
    const lastExit = Number(localStorage.getItem(LAST_EXIT_KEY)) || 0;
    if (!lastExit) return 'cold';            // первый визит
    return Date.now() - lastExit > COLD_THRESHOLD_MS ? 'cold' : 'warm';
  } catch {
    return 'cold';                           // приватный режим без localStorage
  }
}

function saveLastExit() {
  try { localStorage.setItem(LAST_EXIT_KEY, String(Date.now())); } catch {}
}

// =====================================================================
//  ЯДРО: доклеиваем общие параметры к каждому событию (как logEventSession)
// =====================================================================
function logEventSession(eventName, params = {}) {
  const merged = {
    ...params,
    pt_session_id: SessionTracker.sessionId,
    pt_user_id: userContext.userId,
    linked_account_id: userContext.linkedId, // null, если QR-связки нет
    app_version: APP_VERSION,
    platform: 'web',
    user_agent: getBrowserInfo(),
    //build_type: BUILD_TYPE,
    build_type: "release",
    user_status: userContext.isSubscribed ? 'pro' : 'free',
  };

  // analytics инициализируется асинхронно (isSupported), поэтому ждём готовности
  if (analytics) {
    logEvent(analytics, eventName, merged);
  } else {
    analyticsReady.then((a) => {
      if (a) logEvent(a, eventName, merged);
    });
  }
}








// =====================================================================
//  СОБЫТИЯ (порт методов FirebaseBrain)
// =====================================================================

export const FirebaseBrain = {

  // ---------- WEB-ONLY: боковое меню ----------
  webMenuClick(menuItem, isLocked = false) {
    logEventSession('web_menu_click', {
      web_menu_item: menuItem,
      web_menu_locked: isLocked ? 'yes' : 'no',
    });
  },

  // ---------- MAIN ----------
  getReadElement(source, elementId) {
    logEventSession('start_read_element', {
      read_element_from: source,
      element_id: elementId,
    });
  },

  gridScreenClick(buttonId) {
    logEventSession('grid_button_clicked', { button_id: buttonId });
  },

  // ---------- DIALOG ----------
  dialogProClick(dialogId, dialogProFeatureId) {
    logEventSession('dialog_pro_click', {
      dialog_id: dialogId,
      dialog_feature_id: dialogProFeatureId,
    });
  },

  // ---------- TABLES ----------
  tablesOpenClick(tablesId, isProTable) {
    logEventSession('tables_list_open', {
      tables_id: tablesId,
      tables_pro: isProTable ? 'yes' : 'no',
    });
  },

  // ---------- READ ELEMENT ----------
  readElementNavigation(elementNumber, readDirection) {
    logEventSession('element_switch', {
      element_number: elementNumber,
      read_direction: readDirection,
    });
  },

  /* readSocialClick(readSocialName) {
    logEventSession('read_social_click', { read_social_name: readSocialName });
  }, */

  /* readTranslate(readTranslateAnswer) {
    logEventSession('read_translate', {
      read_translate_answer: readTranslateAnswer,
      read_translate_locale:
        typeof navigator !== 'undefined' ? navigator.language : 'unknown',
    });
  }, */

  /* readShareFriend(userChoice, shareVersion) {
    logEventSession('read_share_friend', {
      read_shared_with_friend_activity: FirebaseBrainScreens.READ_ELEMENT_ACTIVITY,
      read_share_with_firiend_version: shareVersion,
      read_shared_with_friend: userChoice ? 'yes' : 'no',
    });
  }, */

  readClickProItem(itemId, itemName, sectionName, elementId) {
    logEventSession('read_click_pro_item', {
      read_click_pro_item_activity: FirebaseBrainScreens.READ_ELEMENT_ACTIVITY,
      read_click_pro_item_id: itemId,
      read_click_pro_item_name: itemName,
      read_click_pro_section: sectionName,
      read_click_pro_element_id: elementId,
    });
  },

  readElementClicks(readSectorName) {
    logEventSession('read_element_click', {
      read_activity_name: FirebaseBrainScreens.READ_ELEMENT_ACTIVITY,
      read_sector_name: readSectorName,
    });
  },

  // ---------- COMPARE ----------
  compareElementClicks(compareSectorName) {
    logEventSession('compare_element_click', {
      compare_activity_name: FirebaseBrainScreens.COMPARE_ELEMENT_ACTIVITY,
      compare_sector_name: compareSectorName,
    });
  },

  //-------------QR-MODAL---------
  qrModalOpen(source, elementId = null) {
    const params = { qr_source: source };
    if (elementId !== null) params.element_id = elementId; // для read — itemId (атомный номер, как read_click_pro_item на Android)
    logEventSession('qr_modal_open', params);
  },

  // Успешное сканирование QR-кода приложением
  qrModalAuthSuccess() {
    logEventSession('qr_modal_auth_success');
  },

  // Закрытие модального окна юзером
  qrModalClosed(isAuthenticated) {
    logEventSession('qr_modal_closed', {
      // Это позволит легко посчитать % отказов (abandoned) в аналитике
      qr_auth_status: isAuthenticated ? 'success' : 'abandoned',
    });
  },

  // Опционально: если сокет не смог подключиться, полезно знать технический loss
  qrModalConnectionError() {
    logEventSession('qr_modal_connection_error');
  },

  // ---------- PAYWALL ----------
  /* _paywallShownAt: 0,

  paywallStart(paywallVersion, proFeature) {
    this._paywallShownAt = Date.now();
    logEventSession('paywall_start', {
      paywall_version: paywallVersion,
      paywall_pro_feature2: String(proFeature).toLowerCase(),
    });
  },

  paywallDismissed(paywallVersion, proFeature) {
    const duration = this._paywallShownAt ? Date.now() - this._paywallShownAt : 0;
    this._paywallShownAt = 0;
    logEventSession('paywall_dismissed', {
      paywall_version: paywallVersion,
      paywall_pro_feature2: String(proFeature).toLowerCase(),
      paywall_duration_ms: duration,
    });
  },

  paywallSubscriptionPlanClick(source, featureTrigger, planPosition, planDuration) {
    logEventSession('paywall_plan_clicked', {
      paywall_screen_source: source,
      paywall_feature_trigger: featureTrigger,
      paywall_plan_position: planPosition,
      paywall_plan_duration: planDuration,
    });
  }, */

  // ---------- PDF ----------
  /* pdfGeneratorStart(elementId) {
    logEventSession('pdf_generation_start', { pdf_element_id: elementId });
  },
  pdfGeneratorSuccess(elementId) {
    logEventSession('pdf_generation_success', { pdf_element_id: elementId });
  },
  pdfGeneratorError(elementId) {
    logEventSession('pdf_generation_error', { pdf_element_id: elementId });
  }, */

  // ---------- SESSION (логируются без полного набора общих параметров) ----------
  _sessionEnded: false,        // защита от дубля session_ended за один уход в фон
  _listenersInstalled: false,  // защита от повторной установки слушателей (StrictMode)

  logSessionStarted(deviceLang, coldWarmSession) {
    const launchType = coldWarmSession || detectLaunchType(); // авто-детект, если не передан
    this._sessionEnded = false;              // новая сессия — снова разрешаем end
    const send = (a) => logEvent(a, 'session_started', {
      pt_session_id: SessionTracker.sessionId,
      device_language: deviceLang,
      cold_warm_session: launchType,
      user_status: userContext.isSubscribed ? 'pro' : 'free',
    });
    if (analytics) send(analytics);
    else analyticsReady.then((a) => a && send(a));
  },

  logSessionEnded() {
    if (this._sessionEnded) return;          // не дублируем за один уход в фон
    this._sessionEnded = true;
    saveLastExit();                          // ОБЯЗАТЕЛЬНО до выхода — нужно для cold/warm
    if (!analytics) return;
    logEvent(analytics, 'session_ended', {
      pt_session_id: SessionTracker.sessionId,
      session_duration_ms: SessionTracker.getSessionDurationMs(),
    });
  },

  // Вызывается один раз при старте приложения. Вешает слушатели завершения сессии:
  // visibilitychange(hidden) — основной сигнал, pagehide — запасной (iOS Safari).
  installSessionListeners() {
    if (this._listenersInstalled) return;    // защита от StrictMode / повторного вызова
    if (typeof document === 'undefined') return;
    this._listenersInstalled = true;

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this.logSessionEnded();
      else this._sessionEnded = false;       // вернулись на вкладку — разрешаем след. end
    });
    window.addEventListener('pagehide', () => this.logSessionEnded());
  },
};